/**
 * FinaleStep — финал онбординга.
 *
 * Показывает "Вы заработали $847 в демо" + "Welcome bonus $1 зачислен"
 * (если bonus_received был false) + финальное CTA "Купить первый слот".
 *
 * Анимация:
 *   - Jarvis-аватарка + реплика вверху
 *   - Конфетти burst на монтировании
 *   - Count-up $1 на реальный баланс
 *   - CTA fade in
 */
import { useEffect } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { CountUpNumber } from '../CountUpNumber';
import { SPRING_CARD } from '../springs';
import { useSounds } from '../useSounds';

interface FinaleStepProps {
  bonusInfo: { credited: boolean; newBalance: string } | null;
  onFinish: () => void;
}

export function FinaleStep({ bonusInfo, onFinish }: FinaleStepProps) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const sounds = useSounds();

  useEffect(() => {
    haptic.success();
    sounds.milestone();
    // Монетки звон при appearance
    window.setTimeout(() => sounds.coin(), 400);
    window.setTimeout(() => sounds.coin(), 700);
  }, [haptic, sounds]);

  const credited = bonusInfo?.credited ?? false;

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center px-5 py-12 gap-6">
      {/* Конфетти-burst */}
      <motion.img
        src="/onboarding/confetti_burst.webp"
        alt=""
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.8, scale: 1.2 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        style={{ mixBlendMode: 'screen' }}
      />

      {/* Jarvis */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_CARD, delay: 0.1 }}
        className="relative z-10 flex items-center gap-3 p-3 pr-4 rounded-full"
        style={{
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <img
          src="/onboarding/jarvis_avatar.webp"
          alt="Jarvis"
          className="w-10 h-10 rounded-full object-cover"
          style={{ boxShadow: '0 0 14px rgba(120,180,255,0.5)' }}
        />
        <div className="text-left">
          <p
            className="text-[10px] font-mono"
            style={{
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'rgba(220,230,255,0.6)',
            }}
          >
            {t('onboarding.finale.jarvis')}
          </p>
          <p
            className="text-[12px] font-semibold"
            style={{ color: 'rgba(240,246,255,0.94)' }}
          >
            {t('onboarding.finale.jarvisLine')}
          </p>
        </div>
      </motion.div>

      {/* Welcome bonus — на glass-подложке, чтобы текст читался поверх конфетти */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_CARD, delay: 0.3 }}
        className="relative z-10 w-full max-w-[340px] px-6 py-5 text-center rounded-[22px]"
        style={{
          background: 'rgba(10,14,28,0.72)',
          backdropFilter: 'blur(22px) saturate(170%)',
          WebkitBackdropFilter: 'blur(22px) saturate(170%)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow:
            '0 18px 48px -16px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.14)',
        }}
      >
        <p
          className="font-mono"
          style={{
            fontSize: 10,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'rgba(220,230,255,0.75)',
          }}
        >
          {credited ? t('onboarding.finale.bonusCredited') : t('onboarding.finale.bonusDone')}
        </p>
        {credited ? (
          <CountUpNumber
            value={1}
            prefix="+$"
            decimals={2}
            delay={0.5}
            className="block mt-2 font-display font-extrabold text-[54px]"
          />
        ) : (
          <p
            className="mt-2 text-[26px] font-extrabold"
            style={{
              color: '#f5e6a8',
              textShadow: '0 0 20px rgba(234,196,96,0.5)',
              letterSpacing: '-0.02em',
            }}
          >
            {t('onboarding.finale.ready')}
          </p>
        )}
        <p
          className="mt-3 text-[13px] leading-relaxed"
          style={{ color: 'rgba(220,230,245,0.82)' }}
        >
          {t('onboarding.finale.body')}
        </p>
        <style>{`
          .font-display { font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif; color: #f5e6a8; text-shadow: 0 0 40px rgba(234,196,96,0.5); }
        `}</style>
      </motion.div>

      {/* CTA */}
      <motion.button
        type="button"
        onClick={() => {
          haptic.medium();
          sounds.swipe();
          onFinish();
        }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_CARD, delay: 1.2 }}
        whileTap={{ scale: 0.97 }}
        className="relative z-10 px-8 py-4 rounded-full font-semibold"
        style={{
          minWidth: 240,
          fontSize: 15,
          color: '#06091c',
          background: 'linear-gradient(180deg, #f5e6a8 0%, #e9c76a 100%)',
          border: '1px solid rgba(255,230,150,0.5)',
          boxShadow:
            '0 10px 30px -8px rgba(234,196,96,0.5), inset 0 1px 0 rgba(255,255,255,0.5)',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {t('onboarding.finale.cta')}
      </motion.button>
    </div>
  );
}
